import { useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { dsSnapshotService } from '@services/dsSnapshotService';
import { dsReviewService } from '@services/dsReviewService';
import { useReviewSse } from '@workpay/ds-shared/hooks/useReviewSse';
import { WorkpayApiError } from '@lib/apiEnvelope';
import type {
  DocumentReview,
  MyReviewInfo,
  ReviewActivity,
} from '@workpay/ds-shared/types/review';
import type { DocumentSnapshot } from '@workpay/ds-shared/types/snapshot';
import type { ReviewerEndReason } from './ReviewerEndedScreen';

export type ReviewerSessionState =
  | { kind: 'loading' }
  | { kind: 'ended'; reason: ReviewerEndReason }
  | { kind: 'ready'; myReview: MyReviewInfo; editorConfig: Record<string, unknown> }
  | { kind: 'ready-loading-editor'; myReview: MyReviewInfo };

interface UseReviewerSessionArgs {
  snapshotId: string;
  snapshot: DocumentSnapshot | undefined;
  /** Reviewer entry intent from URL — hook short-circuits everything when false. */
  enabled: boolean;
}

/**
 * Single source of truth for the reviewer experience on a snapshot.
 * Owns: my-review lookup, editor-config fetch, activity timeline, SSE
 * subscription, submit + complete mutations, end-state derivation.
 *
 * Contract:
 * - If the caller is not a reviewer (404 on my-review) → `ended: not-a-reviewer`
 *   (or `removed` if the session had previously seen a reviewer row — we
 *   remember that with `wasReviewerRef`).
 * - If snapshot is not IN_REVIEW → `ended: not-in-review` unless myReview has
 *   a COMPLETED / CANCELLED status which takes precedence.
 * - `ready` requires both myReview AND editorConfig resolved — avoids mounting
 *   OnlyOffice before the config arrives.
 */
export function useReviewerSession({ snapshotId, snapshot, enabled }: UseReviewerSessionArgs) {
  const queryClient = useQueryClient();

  // Track whether this session ever saw a valid reviewer row — lets us
  // distinguish "removed mid-session" from "never was a reviewer".
  const wasReviewerRef = useRef(false);

  // 1. Resolve current user's reviewer identity on this snapshot.
  //    A non-reviewer caller gets WP_NOT_A_REVIEWER (43052); 4xxx/domain
  //    codes are never retried by the shared queryClient.
  const myReviewQuery = useQuery({
    queryKey: ['dsSnapshot-my-review', snapshotId],
    queryFn: () => dsSnapshotService.getMyReview(snapshotId),
    enabled: enabled,
  });

  const myReview = myReviewQuery.data;
  if (myReview) wasReviewerRef.current = true;

  const myReviewStatus = (() => {
    if (!enabled) return 'disabled' as const;
    if (myReviewQuery.isLoading) return 'loading' as const;
    if (myReviewQuery.isError) {
      // 43052 = WP_NOT_A_REVIEWER (contract-service domain code).
      const code = (myReviewQuery.error as WorkpayApiError).code;
      if (code === 43052) return 'not-a-reviewer' as const;
      return 'error' as const;
    }
    if (!myReview) return 'loading' as const;
    return 'ready' as const;
  })();

  // 2. Editor config — scoped to snapshot, mode=review. Tenant users with
  //    DOCUMENT_SNAPSHOTS_VIEW can call it; backend returns reviewer-shape
  //    config (edit:false, trackChanges:true, stable review_ docKey).
  const editorConfigQuery = useQuery({
    queryKey: ['dsSnapshotEditorConfig', snapshotId, 'review'],
    queryFn: () => dsSnapshotService.getEditorConfig(snapshotId, 'review'),
    enabled:
      enabled &&
      myReviewStatus === 'ready' &&
      snapshot?.templateFormat === 'DOCX',
    staleTime: Infinity, // Never refetch — would remount the OnlyOffice iframe.
  });

  // 3. Activity timeline (chat + system events). Powered by SSE invalidation.
  const activitiesQuery = useQuery({
    queryKey: ['dsSnapshot-reviewer-activities', myReview?.reviewId ?? ''],
    queryFn: () => dsReviewService.getReviewerActivities(myReview!.reviewId),
    enabled: enabled && !!myReview?.reviewId,
    // Poll as a safety net — covers silent SSE disconnects where onerror
    // doesn't fire. 30s is cheap and covers flaky mobile networks.
    refetchInterval: 30_000,
  });

  // 4. SSE — invalidates my-review + activities on events AND on reconnect.
  useReviewSse({
    mode: 'reviewer-internal',
    reviewId: myReview?.reviewId ?? '',
    snapshotId,
    enabled: enabled && !!myReview?.reviewId,
  });

  // 5. Mutations
  const submitMutation = useMutation({
    mutationFn: (message?: string) => dsReviewService.reviewerSubmit(myReview!.reviewId, message),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['dsSnapshot-reviewer-activities', myReview?.reviewId] });
      void queryClient.invalidateQueries({ queryKey: ['dsSnapshot-my-review', snapshotId] });
    },
  });

  const completeMutation = useMutation({
    mutationFn: () => dsReviewService.reviewerComplete(myReview!.reviewId),
    onSuccess: () => {
      // After completion, my-review may 404 (if snapshot transitioned to DRAFT)
      // or return status COMPLETED — both paths flow into the ended screen.
      void queryClient.invalidateQueries({ queryKey: ['dsSnapshot-my-review', snapshotId] });
      void queryClient.invalidateQueries({ queryKey: ['dsSnapshot', snapshotId] });
    },
  });

  const anyPending = submitMutation.isPending || completeMutation.isPending;

  // 6. Synthesize a DocumentReview shape for ChatWidget. It expects an array;
  //    build one from our my-review + activities data.
  const reviewForChat = useMemo<DocumentReview | null>(() => {
    if (!myReview || !snapshot) return null;
    return {
      id: myReview.reviewId,
      targetType: 'TEMPLATE', // Snapshot shares template/envelope chat shape; TEMPLATE is a safe default
      targetId: snapshotId,
      fileId: snapshot.sourceFileId,
      status: 'IN_PROGRESS',
      initiatedById: '',
      initiatedByName: '',
      initiatedByEmail: '',
      authorCompleted: false,
      reviewerCompleted: myReview.status === 'REVIEWED',
      reviewers: [],
      activities: (activitiesQuery.data ?? []) as ReviewActivity[],
      createdOn: new Date().toISOString(),
      updatedOn: new Date().toISOString(),
    };
  }, [myReview, snapshot, snapshotId, activitiesQuery.data]);

  // 7. End-state derivation. Source of truth is `snapshot.status` — a per-reviewer
  //    status of REVIEWED does NOT mean the review is ended. Backend treats
  //    Submit Changes as flipping reviewer.status = REVIEWED (end of this round,
  //    ball in author's court), but `review.reviewer_completed` stays false and
  //    the reviewer can continue interacting. Only snapshot transitioning out of
  //    IN_REVIEW (ACTIVE or INACTIVE) truly ends the review.
  const endReason = useMemo<ReviewerEndReason | null>(() => {
    if (!enabled) return null;
    // Snapshot not loaded yet → still loading, don't end
    if (!snapshot) return null;

    // Explicit "not a reviewer" (or removed mid-session)
    if (myReviewStatus === 'not-a-reviewer') {
      return wasReviewerRef.current ? 'removed' : 'not-a-reviewer';
    }

    // Snapshot left IN_REVIEW — was it completed or cancelled?
    // REVIEW_COMPLETED is the dual-completion parking state; from the reviewer's
    // perspective the review is over (the same as ACTIVE — author hasn't approved
    // yet, but the reviewer has no remaining input).
    if (snapshot.status === 'ACTIVE' || snapshot.status === 'REVIEW_COMPLETED') return 'completed';
    if (snapshot.status === 'INACTIVE') {
      // INACTIVE can mean cancelled OR deactivated — backend doesn't distinguish.
      // "not-in-review" copy is the least presumptuous.
      return 'not-in-review';
    }

    return null;
  }, [enabled, snapshot, myReviewStatus]);

  // 8. Consolidated state
  const state: ReviewerSessionState = (() => {
    if (!enabled) return { kind: 'loading' };
    if (endReason) return { kind: 'ended', reason: endReason };
    if (myReviewStatus !== 'ready') return { kind: 'loading' };
    if (!myReview) return { kind: 'loading' };
    const config = editorConfigQuery.data;
    if (!config) return { kind: 'ready-loading-editor', myReview };
    return { kind: 'ready', myReview, editorConfig: config };
  })();

  return {
    state,
    myReview,
    activities: activitiesQuery.data ?? [],
    reviewForChat,
    submit: submitMutation,
    complete: completeMutation,
    anyPending,
  };
}
